import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AxiosResultDefaultError } from '../../../../api/request';
import { AuthenticationError } from '../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import DisplayInfo from '../../../../components/info/DisplayInfo';
import Loading from '../../../../components/info/Loading';
import searchStudent, { findStudentById } from '../api';

interface BaseLoaderProps {
  searchComponent: ReactElement;
  children: ReactElement;
}

// HU #15313 — the loader handles two entry points with the same context:
//   1. `/current-account`                 — search-by-tipo-ID/ID flow
//   2. `/current-account/students/:id`    — deep-link, fetch by studentId
// The query shape carries either the tipo-ID + ID (+ optional countryId)
// produced by the search form, or `studentId` (deep-link). The request
// callback dispatches based on which field is populated.
type LoaderQuery = {
  identificationTypeId?: string;
  identification?: string;
  countryId?: string | null;
  studentId?: string;
};

interface StudentCurrentAccountData {
  studentId: number;
  rutOrPassport: string;
  name: string;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  LoaderQuery,
  StudentCurrentAccountData,
  AuthenticationError | AxiosResultDefaultError
>();

export function useStudentCurrentAccountLoader() {
  return useFetch();
}

export const studentCurrentAccountLoaderConsumer = FetchConsumer;

const keyPrefix = 'financialPortal.currentAccount.search';

export default function StudentCurrentAccountLoader({
  searchComponent,
  children,
}: BaseLoaderProps) {
  const { t } = useTranslation();
  const { studentId: studentIdParam } = useParams<{ studentId?: string }>();
  const isDeepLink = Boolean(studentIdParam);

  const request = useCallback(async (query: LoaderQuery) => {
    if (query.studentId) {
      return findStudentById(query.studentId);
    }
    // Search flow: the form's submit handler is the only caller. Form
    // validation guarantees both `identificationTypeId` and `identification`
    // are populated before `updateQuery` fires.
    return searchStudent({
      identificationTypeId: query.identificationTypeId ?? '',
      identification: query.identification ?? '',
      countryId: query.countryId,
    });
  }, []);

  const defaultQuery: LoaderQuery = isDeepLink
    ? { studentId: studentIdParam }
    : {};

  return (
    <FetchProvider
      request={request}
      defaultQuery={defaultQuery}
      fetchImmediately={isDeepLink}
    >
      {isDeepLink ? null : searchComponent}
      <FetchConsumer>
        {({ loading, data, error, refresh, query }) => {
          // Deep-link: always loading/error/data — no "empty search" state.
          if (isDeepLink) {
            if (loading) {
              return <Loading insideCard />;
            }
            if (error) {
              return (
                <DisplayError
                  insideCard
                  title={
                    error.status === 404
                      ? t(`${keyPrefix}.error.title`)
                      : undefined
                  }
                  textBody={
                    error.status === 404
                      ? t(`${keyPrefix}.error.body`)
                      : error.code
                  }
                  retryAction={refresh}
                />
              );
            }
            if (data) {
              return children;
            }
            return null;
          }

          // Search flow: the form handles the API call directly and
          // navigates to /:studentId on success. This route only shows
          // the empty-state prompt.
          return (
            <DisplayInfo
              insideCard
              title={t(`${keyPrefix}.emptyState.title`)}
              textBody={t(`${keyPrefix}.emptyState.body`)}
            />
          );
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
